import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe } from '@ngx-translate/core';

import { PRESIDIO_MOCK_NO_AFFILIATE_LOGIN } from 'src/app/extensions/presidio-demo/mock/presidio-mock-session';

/** DEMO-ONLY: prototype account option for click-to-fill on Sign-in. */
export interface PresidioDemoAccount {
  id: string;
  /** Short role name (e.g. Administrator / Standard). */
  roleKey: string;
  /** Affiliate accounts available after login. */
  affiliateKey: string;
  /** Whether the persona can approve purchase requests. */
  approvalKey: string;
  /** Assigned approval contact for this user's purchase rules (display name). */
  approverOfKey: string;
  /** Purchase rules summary for this user (MVP 1.2.4). */
  purchaseRuleKey: string;
  fillAriaKey: string;
  login: string;
  password: string;
  group: 'customer' | 'specialty';
}

/**
 * DEMO-ONLY: one entry per DIAS B2B role (+ warehouse / punchout specialty logins).
 * Fixtures live under assets/mock-data/customers/PRESIDIO-MOCK|PRESIDIO-WH-MOCK/users/.
 * Purchase-rule / approver columns mirror discovery seed (Alex → Morgan).
 */
export const PRESIDIO_DEMO_ACCOUNTS: readonly PresidioDemoAccount[] = [
  {
    id: 'admin',
    roleKey: 'login.demo_admin',
    affiliateKey: 'login.demo_affiliate.admin',
    approvalKey: 'login.demo_approval.yes',
    approverOfKey: 'login.demo_approver_of.none',
    purchaseRuleKey: 'login.demo_rules.none',
    fillAriaKey: 'login.demo_fill.admin.aria',
    login: 'jordan.hale@acme.com',
    password: 'presidio',
    group: 'customer',
  },
  {
    id: 'account_owner',
    roleKey: 'login.demo_account_owner',
    affiliateKey: 'login.demo_affiliate.account_owner',
    approvalKey: 'login.demo_approval.no',
    approverOfKey: 'login.demo_approver_of.none',
    purchaseRuleKey: 'login.demo_rules.none',
    fillAriaKey: 'login.demo_fill.account_owner.aria',
    login: 'sam.patel@acme.com',
    password: 'presidio',
    group: 'customer',
  },
  {
    id: 'buyer',
    roleKey: 'login.demo_buyer',
    affiliateKey: 'login.demo_affiliate.buyer',
    approvalKey: 'login.demo_approval.no',
    approverOfKey: 'login.demo_approver_of.morgan',
    purchaseRuleKey: 'login.demo_rules.buyer',
    fillAriaKey: 'login.demo_fill.buyer.aria',
    login: 'alex.rivera@acme.com',
    password: 'presidio',
    group: 'customer',
  },
  {
    id: 'no_affiliate',
    roleKey: 'login.demo_no_affiliate',
    affiliateKey: 'login.demo_affiliate.no_affiliate',
    approvalKey: 'login.demo_approval.no',
    approverOfKey: 'login.demo_approver_of.none',
    purchaseRuleKey: 'login.demo_rules.none',
    fillAriaKey: 'login.demo_fill.no_affiliate.aria',
    login: PRESIDIO_MOCK_NO_AFFILIATE_LOGIN,
    password: 'presidio',
    group: 'customer',
  },
  {
    id: 'approver',
    roleKey: 'login.demo_approver',
    affiliateKey: 'login.demo_affiliate.approver',
    approvalKey: 'login.demo_approval.yes',
    approverOfKey: 'login.demo_approver_of.none',
    purchaseRuleKey: 'login.demo_rules.none',
    fillAriaKey: 'login.demo_fill.approver.aria',
    login: 'morgan.chen@acme.com',
    password: 'presidio',
    group: 'customer',
  },
  {
    id: 'warehouse',
    roleKey: 'login.demo_warehouse',
    affiliateKey: 'login.demo_affiliate.warehouse',
    approvalKey: 'login.demo_approval.na',
    approverOfKey: 'login.demo_approver_of.na',
    purchaseRuleKey: 'login.demo_rules.na',
    fillAriaKey: 'login.demo_fill.warehouse.aria',
    login: 'casey.ware@presidio-wh.com',
    password: 'presidio',
    group: 'specialty',
  },
  {
    id: 'punchout',
    roleKey: 'login.demo_punchout',
    affiliateKey: 'login.demo_affiliate.punchout',
    approvalKey: 'login.demo_approval.na',
    approverOfKey: 'login.demo_approver_of.na',
    purchaseRuleKey: 'login.demo_rules.na',
    fillAriaKey: 'login.demo_fill.punchout.aria',
    login: 'pat.punch@acme.com',
    password: 'presidio',
    group: 'specialty',
  },
  {
    id: 'sales_rep',
    roleKey: 'login.demo_sales_rep',
    affiliateKey: 'login.demo_affiliate.sales_rep',
    approvalKey: 'login.demo_approval.na',
    approverOfKey: 'login.demo_approver_of.na',
    purchaseRuleKey: 'login.demo_rules.na',
    fillAriaKey: 'login.demo_fill.sales_rep.aria',
    login: 'riley.sales@presidio.com',
    password: 'presidio',
    group: 'specialty',
  },
  {
    id: 'csr',
    roleKey: 'login.demo_csr',
    affiliateKey: 'login.demo_affiliate.csr',
    approvalKey: 'login.demo_approval.na',
    approverOfKey: 'login.demo_approver_of.na',
    purchaseRuleKey: 'login.demo_rules.na',
    fillAriaKey: 'login.demo_fill.csr.aria',
    login: 'quinn.csr@presidio.com',
    password: 'presidio',
    group: 'specialty',
  },
];

export interface PresidioDemoAccountGroup {
  id: 'customer' | 'specialty';
  titleKey: string;
  accounts: readonly PresidioDemoAccount[];
}

/**
 * DEMO-ONLY — prototype picker for demo Sign-in credentials.
 */
@Component({
  selector: 'ish-demo-credentials-modal',
  templateUrl: './demo-credentials-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslatePipe],
})
export class DemoCredentialsModalComponent {
  private readonly activeModal = inject(NgbActiveModal);

  readonly accounts = PRESIDIO_DEMO_ACCOUNTS;

  readonly accountGroups: readonly PresidioDemoAccountGroup[] = [
    {
      id: 'customer',
      titleKey: 'login.demo_group.customer',
      accounts: PRESIDIO_DEMO_ACCOUNTS.filter(a => a.group === 'customer'),
    },
    {
      id: 'specialty',
      titleKey: 'login.demo_group.specialty',
      accounts: PRESIDIO_DEMO_ACCOUNTS.filter(a => a.group === 'specialty'),
    },
  ];

  select(account: PresidioDemoAccount): void {
    this.activeModal.close(account);
  }

  hide(): void {
    this.activeModal.dismiss();
  }
}
